import Box from '@mui/material/Box';
import { t } from 'i18next';
import { sortedIndex } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

import Tabs, { TabProps } from '~components/Tabs/Tabs';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useIndicatorValues } from '~pages/LiveMap/Timeline/components/hooks/useIndicatorValues';

import { DistanceCycleTimeline } from './DistanceCycleTimeline';
import { DistanceJobsTimeline, type JobTimelineEntry } from './DistanceJobsTimeline';
import { DistanceSpeedTimeline } from './DistanceSpeedTimeline';

enum TimelineType {
  CYCLE = 'cycle',
  SPEED = 'speed',
}

const tabs: TabProps<TimelineType>[] = [
  { label: t('live_map.timeline.cycle'), value: TimelineType.CYCLE },
  { label: t('live_map.timeline.speed'), value: TimelineType.SPEED },
];

interface Props {
  onHover?: (index: number) => void;
  onTimeChange?: (time: number) => void;
  pings: LocationHistoryMinimal[];
  jobs?: JobTimelineEntry[];
}

export const DistanceTimeline = ({ onHover, onTimeChange, pings, jobs }: Props) => {
  const [timelineType, setTimelineType] = useState(TimelineType.CYCLE);
  const [currentPing, setCurrentPing] = useState<LocationHistoryMinimal | null>(null);

  const minTime = pings[0].occurredAt.toISOString();
  const maxTime = pings[pings.length - 1].occurredAt.toISOString();

  const { indicatorValues } = useIndicatorValues(minTime, maxTime, []);

  // Sorted array of ping timestamps for binary search (pings are already
  // chronological, so this just extracts the numeric values).
  const pingTimes = useMemo(() => pings.map((p) => p.occurredAt.valueOf()), [pings]);

  useEffect(() => {
    if (!indicatorValues.indicatorTime) {
      return;
    }

    const time = indicatorValues.indicatorTime;
    onTimeChange?.(time);
    const idx = sortedIndex(pingTimes, time);

    if (idx === 0) {
      setCurrentPing(pings[0]);
      onHover?.(0);
      return;
    }

    if (idx >= pings.length) {
      setCurrentPing(pings[pings.length - 1]);
      onHover?.(pings.length - 1);
      return;
    }

    const beforeTime = pingTimes[idx - 1];
    const afterTime = pingTimes[idx];
    const fraction =
      afterTime === beforeTime ? 0 : (time - beforeTime) / (afterTime - beforeTime);
    const fractionalIdx = idx - 1 + fraction;

    setCurrentPing(pings[Math.round(fractionalIdx)]);
    onHover?.(fractionalIdx);
  }, [indicatorValues.indicatorTime, onHover, onTimeChange, pings, pingTimes]);

  if (jobs) {
    return (
      <Box sx={{ mx: 1, mt: 1.5, mb: '25px', position: 'relative' }}>
        <DistanceJobsTimeline pings={pings} jobs={jobs} />
      </Box>
    );
  }

  return (
    <Box sx={{ mx: 1, mt: 1.5, mb: '25px', position: 'relative' }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          height: 'fit-content',
          justifyContent: 'space-between',
          position: 'absolute',
          right: '-4px',
          top: '-4px',
        }}
      >
        <Tabs
          onChange={(value) => setTimelineType(value)}
          selected={timelineType}
          tabs={tabs}
          sx={{
            '& .MuiButtonBase-root': {
              minWidth: '60px',
              height: '25px !important',
            },
          }}
        />
      </Box>

      {timelineType === TimelineType.CYCLE ? (
        <DistanceCycleTimeline currentPing={currentPing} pings={pings} />
      ) : (
        <DistanceSpeedTimeline currentPing={currentPing} pings={pings} />
      )}
    </Box>
  );
};
